import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import { Admin } from "../../../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  NPSAnalyticsFilters,
  NPSAnalyticsSelectedFilters,
  parseQueryParamsForNPSAnalyticsFilters,
} from "../../../../../models/Dashboard/NPSDashboard/NPSAnalyticsFilters";
import TypeFilterCard from "../../components/filters/TypeFilterCard";

const NPS_ANALYTICS_FILTERS_KEY = "NPS_ANALYTICS_FILTERS_KEY";

const NPSAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    npsAnalyticsDashboardStore,
    projectStore,
    TaskTrackerStore,
    userStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: NPSAnalyticsFilters =
    parseQueryParamsForNPSAnalyticsFilters(
      localStorage.getItem(NPS_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(NPS_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );

  const [filters, setFilters] = useState<NPSAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<NPSAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(NPS_ANALYTICS_FILTERS_KEY, JSON.stringify(filters));

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.pm_admin_id) {
      filters.pm_admin_id.forEach((pm_admin_id) =>
        newFilters.push({ id: "pm_admin_id", value: pm_admin_id.value })
      );
    }

    if (filters.order_type) {
      filters.order_type.forEach((order_type) =>
        newFilters.push({ id: "order_type", value: order_type.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await npsAnalyticsDashboardStore.getArchivedProjectsAnalyticsTats({
        pm_admin_id: selectedFilters.pm_admin_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });

      await npsAnalyticsDashboardStore.getNPSAnalyticsTats({
        pm_admin_id: selectedFilters.pm_admin_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "pm_admin_id":
        setFilters((prev) => ({
          ...prev,
          pm_admin_id:
            filters.pm_admin_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_type":
        setFilters((prev) => ({
          ...prev,
          order_type:
            prev.order_type?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_admin_id: [],
      order_type: [],
    }));

    setSelectedFiltersData([]);
  };

  const handleAdminSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText.length > 2) {
      if (apiTimer) clearTimeout(apiTimer);

      apiTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
          showError("Error", err?.response?.data?.message);
        });
      }, 1500);
    }
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_NPS_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 1,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Project Manager"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Poc"
                    fontSize="0.9rem"
                    value={filters.pm_admin_id || []}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(pm_admin_id) =>
                      handleAdminSearch(pm_admin_id!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        pm_admin_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Order Type"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    isMultiSelect={true}
                    valuesList={
                      filters.order_type?.map((item) => item.id) || []
                    }
                    list={
                      projectStore?.orderType?.filter((item) => item.id !== "2").map((order) => ({
                        id: order.id.toString(),
                        value: order.value,
                      })) || []
                    }
                    fontSize={"12px"}
                    onMultiSelectionChange={(ids) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_type:
                          projectStore?.orderType?.filter((item) =>
                            ids.includes(item.id)
                          ) || [],
                      }));
                    }}
                  />
                </Grid2>
                <Grid2
                  size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>
            <Box>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>
            <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
              {npsAnalyticsDashboardStore.basic_tats?.length > 0 && (
                <Box sx={{ gap: 2, display: "flex", flexDirection: "row" }}>
                  <hr style={{ height: 1, width: "100%" }} />
                  <CustomTypography
                    variant="heading7"
                    color={theme.palette.customColors.blackBorder}
                    style={{
                      minWidth: "150px",
                      textAlign: "center",
                    }}
                  >
                    Archived Projects
                  </CustomTypography>
                  <hr style={{ height: 1, width: "100%" }} />
                </Box>
              )}
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                The date filter is applied on the project archived date.
              </CustomTypography>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {npsAnalyticsDashboardStore.basic_tats.map((item, index) => (
                  <Box
                    key={`${item.label}-${index}`}
                    sx={{
                      flex: {
                        xs: "1 1 140px",
                        sm: "1 1 180px",
                        md: "1 1 200px",
                      },
                      maxWidth: {
                        xs: "calc(50% - 8px)",
                        sm: "calc(33.33% - 16px)",
                        md: "calc(25% - 16px)",
                        lg: "calc(20% - 16px)",
                        xl: "calc(16.66% - 16px)",
                      },
                      minWidth: { xs: "140px", sm: "160px", md: "180px" },
                    }}
                  >
                    <TypeFilterCard
                      title={item.label}
                      message={item.info}
                      value={item.value ?? "0"}
                      color={""}
                      icon={
                        "https://content.swageazy.com/icons/boxes_green.png"
                      }
                      onClick={(title) => {
                        switch (title) {
                          case "Archived Orders Count":
                            const archived_orders_count_url = `/report/data?title=${encodeURI(
                              "Archived Orders Count"
                            )}&base_url=/v5/nps-analytics&path=/data/archived-projects&${searchParams.toString()}`;
                            window.open(archived_orders_count_url, "_blank");
                            break;
                          default:
                            break;
                        }
                      }}
                    />
                  </Box>
                ))}
              </Grid2>
              {npsAnalyticsDashboardStore.nps_tats?.length > 0 && (
                <Box sx={{ gap: 2, display: "flex", flexDirection: "row" }}>
                  <hr style={{ height: 1, width: "100%" }} />
                  <CustomTypography
                    variant="heading7"
                    color={theme.palette.customColors.blackBorder}
                    style={{
                      minWidth: "150px",
                      textAlign: "center",
                    }}
                  >
                    NPS Form Details
                  </CustomTypography>
                  <hr style={{ height: 1, width: "100%" }} />
                </Box>
              )}
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                The date filter is applied on the NPS form submission date.
              </CustomTypography>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {npsAnalyticsDashboardStore.nps_tats.map((item, index) => (
                  <Box
                    key={`${item.label}-${index}`}
                    sx={{
                      flex: {
                        xs: "1 1 140px",
                        sm: "1 1 180px",
                        md: "1 1 200px",
                      },
                      maxWidth: {
                        xs: "calc(50% - 8px)",
                        sm: "calc(33.33% - 16px)",
                        md: "calc(25% - 16px)",
                        lg: "calc(20% - 16px)",
                        xl: "calc(16.66% - 16px)",
                      },
                      minWidth: { xs: "140px", sm: "160px", md: "180px" },
                    }}
                  >
                    <TypeFilterCard
                      title={item.label}
                      message={item.info}
                      value={item.value ?? "0"}
                      color={""}
                      icon={
                        "https://content.swageazy.com/icons/boxes_green.png"
                      }
                      onClick={(title) => {
                        switch (title) {
                          case "Total responses received":
                            const total_responses_received_url = `/report/data?title=${encodeURI(
                              "NPS Form Responses"
                            )}&base_url=/v5/nps-analytics&path=/data/nps-form-details&${searchParams.toString()}`;
                            window.open(total_responses_received_url, "_blank");
                            break;
                          case "NPS Score":
                            const nps_score_url = `/report/data?title=${encodeURI(
                              "NPS Score"
                            )}&base_url=/v5/nps-analytics&path=/data/nps-form-answers&${searchParams.toString()}`;
                            window.open(nps_score_url, "_blank");
                            break;
                          default:
                            break;
                        }
                      }}
                    />
                  </Box>
                ))}
              </Grid2>
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(NPSAnalyticsDashboard);
